import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UserPlus, Crown, Shield, User, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface FamilyInvitation {
  id: string;
  family_group_id: string;
  name: string;
  email: string;
  role: string;
  status: string;
}

interface FamilyGroup {
  id: string;
  name: string;
  description: string;
  owner_id: string;
}

export const FamilyInvitePage = () => {
  const { invitationId } = useParams();
  const navigate = useNavigate();
  const { user, signInWithGoogle } = useAuth();
  const [invitation, setInvitation] = useState<FamilyInvitation | null>(null);
  const [familyGroup, setFamilyGroup] = useState<FamilyGroup | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    loadInvitation();
  }, [invitationId]);

  // Remove auto-accept - let user explicitly accept after login

  const loadInvitation = async () => {
    if (!invitationId) {
      console.error('No invitation ID provided');
      setLoading(false);
      return;
    }

    try {
      console.log('Loading invitation:', invitationId);
      
      // First try to find pending invitation
      const { data: pendingData, error: pendingError } = await supabase
        .from('family_memberships')
        .select('*')
        .eq('id', invitationId)
        .eq('status', 'pending')
        .maybeSingle();

      console.log('Pending invitation query result:', { pendingData, pendingError });

      if (pendingData) {
        setInvitation(pendingData);
        
        // Now load the family group details separately
        const { data: familyGroupData, error: familyGroupError } = await supabase
          .from('family_groups')
          .select('*')
          .eq('id', pendingData.family_group_id)
          .maybeSingle();

        console.log('Family group query result:', { familyGroupData, familyGroupError });

        if (familyGroupData) {
          setFamilyGroup(familyGroupData);
        }
        
        setLoading(false);
        return;
      }

      // If no pending invitation found, check if it was already accepted
      const { data: activeData, error: activeError } = await supabase
        .from('family_memberships')
        .select('*')
        .eq('id', invitationId)
        .eq('status', 'active')
        .maybeSingle();

      console.log('Active invitation query result:', { activeData, activeError });

      if (activeData) {
        // Invitation was already accepted, redirect to family page
        toast.success('You are already a member of this family group!');
        navigate('/family');
        return;
      }

      // No invitation found at all
      console.error('No invitation found with ID:', invitationId);
      throw new Error('Invitation not found');
    } catch (error) {
      console.error('Error loading invitation:', error);
      toast.error('Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const acceptInvitation = async () => {
    if (!invitation || !user) return;

    setAccepting(true);
    try {
      // Update the specific invitation by ID
      const { error } = await supabase
        .from('family_memberships')
        .update({
          user_id: user.id,
          status: 'active',
          joined_at: new Date().toISOString()
        })
        .eq('id', invitation.id);

      if (error) throw error;

      toast.success('Welcome to the family group!');
      navigate('/family');
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast.error('Failed to join family group');
    } finally {
      setAccepting(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Crown className="h-5 w-5 text-yellow-500" />;
      case 'admin':
        return <Shield className="h-5 w-5 text-blue-500" />;
      default:
        return <User className="h-5 w-5 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">Invitation not found or expired</p>
            <Button 
              onClick={() => navigate('/')} 
              className="mt-4"
            >
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <UserPlus className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Family Group Invitation</CardTitle>
          <CardDescription>
            You've been invited to join a family group on Event Date Picker
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-muted/50 p-4 rounded-lg">
            <h3 className="font-semibold text-lg mb-2">{familyGroup?.name || 'Loading...'}</h3>
            {familyGroup?.description && (
              <p className="text-muted-foreground text-sm mb-3">
                {familyGroup.description}
              </p>
            )}
            <div className="flex items-center gap-2">
              {getRoleIcon(invitation.role)}
              <span className="text-sm">
                You'll join as: <Badge variant="outline" className="ml-1">{invitation.role}</Badge>
              </span>
            </div>
          </div>

          {!user ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                To join this family group, please sign in or create an account
              </p>
              <Button 
                onClick={() => signInWithGoogle()} 
                className="w-full"
                size="lg"
              >
                Continue with Google
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-green-800">
                  Signed in as {user.email}
                </span>
              </div>
              <Button 
                onClick={acceptInvitation} 
                disabled={accepting}
                className="w-full"
                size="lg"
              >
                {accepting ? 'Joining...' : 'Join Family Group'}
              </Button>
            </div>
          )}

          <div className="text-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/')}
            >
              Maybe Later
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};